import { faRotateLeft } from '@fortawesome/pro-regular-svg-icons';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  RadioButton,
  Text,
  TextButton,
  TextWithIcon,
} from '@modusign/design-system';
import { listMembers } from '@modusign/openapi-client-user';
import { useState } from 'react';
import { useQuery } from 'react-query';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import { WindowModal, WindowModalContent } from '@app/components/Modal/WindowModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useCurrentUserMemberId } from '@app/hooks/workspace';

type Member = { id: string; name: string; userEmail: string };

interface MemberSelectModalProps {
  title: string;
  onSelect: (member: Member) => void;
  onClose: () => void;
}

export default function MemberSelectModal({ title, onSelect, onClose }: MemberSelectModalProps) {
  const [selectedMember, setSelectedMember] = useState<null | Member>(null);
  const [keyword, setKeyword] = useState('');
  const { data, isLoading, isError, refetch } = useMemberList({ keyword });
  const currentMemberId = useCurrentUserMemberId();

  return (
    <WindowModal isOpen maxWidth="sm">
      <Flex
        alignItems="center"
        borderBottom="1px solid"
        borderBottomColor="modalBorderNeutralBase"
        justifyContent="space-between"
        p={6}
        width="100%"
      >
        <Heading fontSize="lg" fontWeight="medium">
          {title}
        </Heading>
        <Flex
          alignItems="center"
          as="button"
          color="textNeutralLight"
          fontSize="18px"
          height="20px"
          type="button"
          width="20px"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faXmark} />
        </Flex>
      </Flex>
      <WindowModalContent>
        <Input
          placeholder="이름 또는 이메일로 검색"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Flex justifyContent="space-between" mt={8}>
          <Text fontWeight="medium">
            {keyword ? `'${keyword}' 검색 결과` : '전체 멤버'}
            <Text
              bg="statusBackgroundNeutralBase"
              color="textNeutralLight"
              fontSize="xxs"
              fontWeight="medium"
              ml="2px"
              sx={{ borderRadius: '10px', px: 2, py: '2px' }}
            >
              {data?.length}
            </Text>
          </Text>
          {keyword && (
            <TextButton onClick={() => setKeyword('')}>
              <TextWithIcon
                iconContainerWidth={12}
                leftIcon={<FontAwesomeIcon icon={faRotateLeft} />}
              >
                검색 초기화
              </TextWithIcon>
            </TextButton>
          )}
        </Flex>
        {isLoading && (
          <Box position="relative">
            <CenterLoadingSpinner />
          </Box>
        )}
        {isError && (
          <Box position="relative">
            불러오기 실패
            <TextButton ml={2} onClick={() => refetch()}>
              다시 시도하기
            </TextButton>
          </Box>
        )}
        <Flex
          as="ul"
          borderTop="1px solid"
          borderTopColor="tableBorderNeutralLight"
          flexDirection="column"
          maxHeight="350px"
          mt={4}
          overflowY="auto"
        >
          {data?.map((member) => {
            return (
              <li key={member.id}>
                <Flex
                  alignItems="center"
                  as="label"
                  borderBottom="1px solid"
                  borderBottomColor="tableBorderNeutralLight"
                  p={4}
                  sx={{ '&:hover': { bg: 'tableBackgroundNeutralSlightlyDark' } }}
                >
                  <RadioButton
                    checked={member.id === selectedMember?.id}
                    disabled={member.id === currentMemberId}
                    onClick={() => setSelectedMember(member)}
                  />
                  <Box ml={4}>
                    <Text as="div">{member.name}</Text>
                    <Text as="div" color="textNeutralVeryLight" fontSize="xs">
                      {member.userEmail}
                    </Text>
                  </Box>
                </Flex>
              </li>
            );
          })}
        </Flex>
      </WindowModalContent>
      <Flex justifyContent="flex-end" px={8} py={6} width="100%">
        <Button variant="subtle" onClick={onClose}>
          취소
        </Button>
        <Button
          disabled={!selectedMember}
          ml={3}
          variant="primary"
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          onClick={() => onSelect(selectedMember!)}
        >
          확인
        </Button>
      </Flex>
    </WindowModal>
  );
}

function useMemberList({ keyword }: { keyword: string }) {
  const workspaceId = useWorkspaceId();

  const result = useQuery({
    queryKey: ['memberAllList', workspaceId],
    queryFn: () => listMembers(workspaceId),
  });

  return {
    ...result,
    data: result.data?.members.filter(
      (member) => member.name.includes(keyword) || member.userEmail.includes(keyword)
    ),
  };
}
